import { Collapse } from 'antd';
import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { LoseChangesModal } from 'src/features/modals/lose-changes-modal';
import DeleteIcon from 'src/shared/assets/icons/delete.svg';
import SplitIcon from 'src/shared/assets/icons/split.svg';
import DropdownIcon from 'src/shared/assets/icons/triangle.svg';
import { Button } from 'src/shared/components/button';
import type { Directories } from 'src/store/directories/directories.store';

import { RestoreRigOperationToDirectoryValuesButton } from '../components/restore-approaches-to-directory-values-button';
import type { FormStore } from '../entities/form.entity';
import { RigOperationsViewModel } from '../entities/rig-operations/rig-operations-view-model';

import { RigOperationItem } from './rig-operation-item';
import styles from './rig-operations-tab.module.scss';

type Props = {
  formStore: FormStore;
  directories: Directories;
  onAddWellToChart(): void;
};

export const RigOperationsTab = observer<Props>(function RigOperationsTab({
  formStore,
  directories,
  onAddWellToChart,
}) {
  const { t } = useTranslation();

  const { rigOperationsService } = formStore;
  const [rigOperationsViewModel] = useState(() => new RigOperationsViewModel(formStore, directories));
  const [activePanels, setActivePanels] = useState(() => {
    if (!rigOperationsService.rigOperations.length) {
      return [];
    }

    return rigOperationsService.rigOperations.map((ro) => ro.entityId.toString());
  });

  const {
    isDeleteButtonShown,
    isFormDisabled,
    isLoseChangesModalOpened,
    restoreToDirectoryValueLoadingState,
    approveRigOperationResetToDirectoryValues,
    cancelRigOperationResetToDirectoryValues,
    onValueChange,
    deleteRigOperation,
    restoreRigOperationToDirectoryValues,
  } = rigOperationsViewModel;

  const handleChangeActivePanel = (keys: string | string[]) => {
    if (!Array.isArray(keys)) {
      setActivePanels([keys]);
      return;
    }

    setActivePanels(keys);
  };

  useEffect(() => {
    setActivePanels(rigOperationsService.rigOperations.map((ro) => ro.entityId.toString()));
  }, [rigOperationsService.rigOperations]);

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.approachesList}>
          <Collapse
            onChange={handleChangeActivePanel}
            activeKey={activePanels}
            className={styles.rigOperationsCollapse}
            expandIcon={({ isActive }) => {
              return (
                <div className={styles.expandIconContainer}>
                  <DropdownIcon
                    className={clsx(styles.expandIcon, isActive && styles.expandIconActive, styles.expandIconDisabled)}
                  />
                </div>
              );
            }}
          >
            {rigOperationsService.rigOperations.map((rigOperation, index) => {
              return (
                <Collapse.Panel
                  header={
                    <div className={styles.rigOperationPanelHeader}>
                      <p className={styles.rigOperationPanelHeader__text}>
                        {t('geologicalLoad:labels.rigOperationIndex', {
                          index: rigOperationsService.rigOperations.indexOf(rigOperation) + 1,
                        })}
                      </p>
                      <RestoreRigOperationToDirectoryValuesButton
                        isLoading={restoreToDirectoryValueLoadingState['rigOperation']}
                        onRestoreToDirectoryValues={(e) => {
                          e.stopPropagation();
                          restoreRigOperationToDirectoryValues(rigOperation);
                        }}
                      />
                      {isDeleteButtonShown && (
                        <Button
                          variant="default"
                          className={styles.deleteButton}
                          icon={<DeleteIcon width={18} height={18} />}
                          // TODO: сделать фильтрацию события ну или навесить stopPropagation
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteRigOperation(rigOperation);
                          }}
                        />
                      )}
                    </div>
                  }
                  className={clsx(styles.rigOperationPanel, isDeleteButtonShown && styles.rigOperationPanel_narrowed)}
                  key={rigOperation.entityId.toString()}
                >
                  <RigOperationItem
                    rigOperation={rigOperation}
                    isAddToChartButtonVisible={index === 0}
                    rigOperationsViewModel={rigOperationsViewModel}
                    onValueChange={onValueChange}
                    onAddWellToChart={onAddWellToChart}
                  />
                </Collapse.Panel>
              );
            })}
          </Collapse>
        </div>
        <div className={styles.newRigOperationButtonWrapper}>
          <Button
            prefixIcon={<SplitIcon width={16} height={16} stroke="white" />}
            variant="success"
            disabled={isFormDisabled}
            onClick={rigOperationsService.addRigOperation}
          >
            <p>{t('newWellForm:Approaches.addNewApproach')}</p>
          </Button>
        </div>
      </div>
      <LoseChangesModal
        text={t('newWellForm:Approaches.stagesLoseChanges')}
        isOpened={isLoseChangesModalOpened}
        confirmCallback={approveRigOperationResetToDirectoryValues}
        handleClose={cancelRigOperationResetToDirectoryValues}
      />
    </>
  );
});
